import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { createStackNavigator } from '@react-navigation/stack';
import {
  NavigationContainer,
  DefaultTheme,
  DarkTheme,
  getFocusedRouteNameFromRoute,
} from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { View, Text, TouchableOpacity, Platform } from 'react-native';
import { useTheme } from '../context/ThemeContext';
import { NotificationListener } from '../components/NotificationListener';
import WebDesktopLayout from '../components/WebDesktopLayout';
import AppLoadingScreen from '../components/AppLoadingScreen';

// Screens
import WelcomeScreen from '../screens/WelcomeScreen';
import LoginScreen from '../screens/LoginScreen';
import DashboardScreen from '../screens/DashboardScreen';
import GoalsScreen from '../screens/GoalsScreen';
import IncomeScreen from '../screens/IncomeScreen';
import ProgressScreen from '../screens/ProgressScreen';
import ReportScreen from '../screens/ReportScreen';
import AnalyticsScreen from '../screens/AnalyticsScreen';
import SettingsScreen from '../screens/SettingsScreen';
import FAQScreen from '../screens/FAQScreen';
import TermsPrivacyScreen from '../screens/TermsPrivacyScreen';
import SubscriptionScreen from '../screens/SubscriptionScreen';
import PinLockScreen from '../screens/PinLockScreen';

// Context
import { AuthProvider, useAuth } from '../context/AuthContext';
import { AppLockProvider, useAppLock } from '../context/AppLockContext';

const Tab = createBottomTabNavigator();
const Stack = createStackNavigator();

const getTabIcon = (route: string, focused: boolean): React.ComponentProps<typeof Ionicons>['name'] => {
  switch (route) {
    case 'Dashboard':
      return focused ? 'home' : 'home-outline';
    case 'Goals':
      return focused ? 'flag' : 'flag-outline';
    case 'Income':
      return focused ? 'wallet' : 'wallet-outline';
    case 'Progress':
      return focused ? 'stats-chart' : 'stats-chart-outline';
    case 'Report':
      return focused ? 'document-text' : 'document-text-outline';
    default:
      return 'help-outline';
  }
};

const LoadingScreen = () => <AppLoadingScreen message="Loading..." />;

// Custom header for Analytics screen
const AnalyticsHeader = () => (
  <View style={{ 
    flexDirection: 'column', 
    alignItems: 'center', 
    justifyContent: 'center',
    paddingVertical: Platform.OS === 'ios' ? 4 : 8,
    paddingHorizontal: 8,
    maxWidth: Platform.OS === 'ios' ? 340 : 350, // Increased width for iOS
    width: '100%',
    flex: 1, // Allow flexible width
    minHeight: Platform.OS === 'ios' ? 48 : 44, // Ensure enough height
  }}>
    <Text style={{ 
      color: '#fff', 
      fontWeight: 'bold', 
      fontSize: Platform.OS === 'ios' ? 16 : 20, // Reduced font size for iOS
      textAlign: 'center',
      marginBottom: Platform.OS === 'ios' ? 1 : 2
    }}>
      Analytics Dashboard
    </Text>
    <Text 
      numberOfLines={Platform.OS === 'ios' ? 2 : 2} // Keep 2 lines but ensure proper wrapping
      style={{ 
        color: 'rgba(255,255,255,0.85)', 
        fontSize: Platform.OS === 'ios' ? 10 : 13, // Smaller font on iOS for better fit
        textAlign: 'center',
        flexWrap: 'wrap',
        width: '100%',
        lineHeight: Platform.OS === 'ios' ? 12 : 16, // Tighter line height for iOS
      }}
    >
      Comprehensive insights into your financial goals
    </Text>
  </View>
);

const DashboardStack = () => {
  const { colors } = useTheme();
  const stackHeaderOptions = useMemo(
    () => ({
      headerStyle: { backgroundColor: colors.primary.header },
      headerTintColor: colors.text.onNav,
      headerTitleStyle: { fontWeight: 'bold' as const, color: colors.text.onNav },
      ...(Platform.OS === 'web' ? { headerTitleAlign: 'left' as const } : {}),
    }),
    [colors]
  );

  return (
    <Stack.Navigator
      screenOptions={
        Platform.OS === 'web'
          ? { contentStyle: { flex: 1 }, headerShown: false }
          : undefined
      }
    >
      <Stack.Screen 
        name="DashboardMain" 
        component={DashboardScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="Settings" 
        component={SettingsScreen}
        options={{
          title: 'Settings',
          ...stackHeaderOptions,
          headerTitleStyle: {
            fontWeight: 'bold',
            fontSize: Platform.OS === 'ios' ? 18 : 20,
          },
          headerBackTitleStyle: {
            fontSize: Platform.OS === 'ios' ? 14 : 16,
          },
        }}
      />
      <Stack.Screen 
        name="Subscription" 
        component={SubscriptionScreen}
        options={{
          title: 'Upgrade Plan',
          ...stackHeaderOptions,
        }}
      />
      <Stack.Screen 
        name="Analytics" 
        component={AnalyticsScreen}
        options={({ navigation }) => ({
          ...stackHeaderOptions,
          headerTitle: () => <AnalyticsHeader />,
          headerLeft: () => (
            <TouchableOpacity
              onPress={() => navigation.goBack()}
              style={{ marginLeft: 16 }}
            >
              <Ionicons name="arrow-back" size={24} color="#fff" />
            </TouchableOpacity>
          ),
        })}
      />
      <Stack.Screen 
        name="FAQ" 
        component={FAQScreen}
        options={{
          title: 'FAQ',
          ...stackHeaderOptions,
          headerTitleStyle: {
            fontWeight: 'bold',
            fontSize: Platform.OS === 'ios' ? 18 : 20,
          },
          headerBackTitleStyle: {
            fontSize: Platform.OS === 'ios' ? 14 : 16,
          },
        }}
      />
      <Stack.Screen 
        name="TermsPrivacy" 
        component={TermsPrivacyScreen}
        options={{
          title: 'Terms & Privacy',
          ...stackHeaderOptions,
          headerTitleStyle: {
            fontWeight: 'bold',
            fontSize: Platform.OS === 'ios' ? 18 : 20,
          },
          headerBackTitleStyle: {
            fontSize: Platform.OS === 'ios' ? 14 : 16,
          },
        }}
      />
    </Stack.Navigator>
  );
};

const getDashboardTabOptions = ({
  route,
}: {
  route: { name: string; state?: { routes: { name: string }[]; index?: number } };
}) => ({
  title: 'Dashboard',
  headerShown: false,
});

const getReportTabOptions = () => ({
  title: 'Report',
  headerShown: false,
});

const ReportStack = () => {
  const { colors } = useTheme();
  const stackHeaderOptions = useMemo(
    () => ({
      headerStyle: { backgroundColor: colors.primary.header },
      headerTintColor: colors.text.onNav,
      headerTitleStyle: { fontWeight: 'bold' as const, color: colors.text.onNav },
      ...(Platform.OS === 'web' ? { headerTitleAlign: 'left' as const } : {}),
    }),
    [colors]
  );

  return (
    <Stack.Navigator
      screenOptions={
        Platform.OS === 'web'
          ? { contentStyle: { flex: 1 }, headerShown: false }
          : undefined
      }
    >
      <Stack.Screen
        name="ReportMain"
        component={ReportScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="Analytics"
        component={AnalyticsScreen}
        options={({ navigation }) => ({
          ...stackHeaderOptions,
          headerTitle: () => <AnalyticsHeader />,
          headerLeft: () => (
            <TouchableOpacity
              onPress={() => navigation.goBack()}
              style={{ marginLeft: 16 }}
            >
              <Ionicons name="arrow-back" size={24} color="#fff" />
            </TouchableOpacity>
          ),
        })}
      />
      <Stack.Screen
        name="Subscription"
        component={SubscriptionScreen}
        options={{
          title: 'Upgrade Plan',
          ...stackHeaderOptions,
        }}
      />
    </Stack.Navigator>
  );
};

const MainTabNavigator = () => {
  const { colors } = useTheme();

  const tabHeaderOptions = useMemo(
    () => ({
      headerStyle: { backgroundColor: colors.primary.header },
      headerTintColor: colors.text.onNav,
      headerTitleStyle: { fontWeight: 'bold' as const, color: colors.text.onNav },
    }),
    [colors]
  );

  const mainTabScreenOptions = useCallback(
    ({ route }: { route: { name: string } }) => ({
      tabBarIcon: ({ focused, color, size }: { focused: boolean; color: string; size: number }) => {
        const iconName = getTabIcon(route.name, focused);
        return <Ionicons name={iconName} size={size} color={color} />;
      },
      tabBarActiveTintColor: colors.tabBar.active,
      tabBarInactiveTintColor: colors.tabBar.inactive,
      tabBarStyle: {
        backgroundColor: colors.tabBar.background,
        borderTopColor: colors.border,
        ...(Platform.OS === 'web'
          ? { display: 'none' as const, height: 0 }
          : {}),
      },
      ...(Platform.OS === 'web'
        ? {
            headerShown: false,
            sceneContainerStyle: { flex: 1 },
          }
        : tabHeaderOptions),
      ...(Platform.OS === 'web'
        ? { tabBarItemStyle: { display: 'none' as const } }
        : {}),
    }),
    [colors, tabHeaderOptions]
  );

  return (
  <Tab.Navigator screenOptions={mainTabScreenOptions}>
    <Tab.Screen
      name="Dashboard"
      component={DashboardStack}
      options={getDashboardTabOptions}
    />
    <Tab.Screen
      name="Goals"
      component={GoalsScreen}
      options={{
        title: 'My Goals',
      }}
    />
    <Tab.Screen
      name="Income"
      component={IncomeScreen}
      options={{
        title: 'Income & Allocation',
      }}
    />
    <Tab.Screen
      name="Progress"
      component={ProgressScreen}
      options={{
        title: 'Progress',
      }}
    />
    <Tab.Screen
      name="Report"
      component={ReportStack}
      options={getReportTabOptions}
    />
  </Tab.Navigator>
  );
};

const AuthenticatedStack = () => {
  if (Platform.OS === 'web') {
    return (
      <WebDesktopLayout>
        <MainTabNavigator />
      </WebDesktopLayout>
    );
  }

  return <MainTabNavigator />;
};

const AuthStack = () => {
  return (
    <Stack.Navigator screenOptions={{ headerShown: false }}>
      <Stack.Screen name="Welcome" component={WelcomeScreen} />
      <Stack.Screen name="Login" component={LoginScreen} />
    </Stack.Navigator>
  );
};

const RootNavigator = () => {
  const { colors } = useTheme();
  const { isAuthenticated, isLoading } = useAuth();
  const { shouldShowAppLock, unlockApp, checkAppLockStatus } = useAppLock();
  const [hasCheckedAppLock, setHasCheckedAppLock] = useState(false);

  // Check app lock status on app start (only once when authenticated)
  useEffect(() => {
    console.log('RootNavigator: useEffect triggered', {
      isAuthenticated,
      isLoading,
      shouldShowAppLock,
      hasCheckedAppLock
    });
    
    if (isAuthenticated && !isLoading && !hasCheckedAppLock) {
      console.log('RootNavigator: Checking app lock status');
      setHasCheckedAppLock(true);
      checkAppLockStatus().catch(error => {
        console.error('RootNavigator: Error checking app lock status:', error);
      });
    }
  }, [isAuthenticated, isLoading, hasCheckedAppLock, checkAppLockStatus]);

  // Reset the check flag when authentication state changes
  useEffect(() => {
    if (!isAuthenticated) {
      setHasCheckedAppLock(false);
    }
  }, [isAuthenticated]);

  console.log('RootNavigator: Rendering with auth state', { 
    isAuthenticated, 
    isLoading,
    shouldShowAppLock,
    hasCheckedAppLock,
    timestamp: new Date().toISOString()
  });

  if (isLoading) {
    console.log('RootNavigator: Showing loading screen');
    return <AppLoadingScreen />;
  }

  // If authenticated but app lock is required, show pin lock screen
  if (isAuthenticated && shouldShowAppLock) {
    console.log('RootNavigator: Showing app lock screen');
    return <PinLockScreen onUnlock={unlockApp} />;
  }

  console.log('RootNavigator: Rendering main navigation', { 
    isAuthenticated
  });

  return (
    <Stack.Navigator 
      screenOptions={{ 
        headerShown: false,
        gestureEnabled: false,
        animationEnabled: true
      }}
    >
      {isAuthenticated ? (
        <Stack.Screen 
          name="Main" 
          component={AuthenticatedStack}
          options={{
            animationEnabled: true
          }}
        />
      ) : (
        <Stack.Screen 
          name="Auth" 
          component={AuthStack}
          options={{
            animationEnabled: true
          }}
        />
      )}
    </Stack.Navigator>
  );
};

const AppNavigator = () => {
  const { colors, resolvedMode } = useTheme();

  const navigationTheme = useMemo(() => {
    const base = resolvedMode === 'dark' ? DarkTheme : DefaultTheme;
    return {
      ...base,
      dark: resolvedMode === 'dark',
      colors: {
        ...base.colors,
        primary: colors.brand.accent,
        background: colors.background.default,
        card: colors.surface,
        text: colors.text.primary,
        border: colors.border,
        notification: colors.accent.mint,
      },
    };
  }, [colors, resolvedMode]);

  return (
    <NavigationContainer theme={navigationTheme}>
      <NotificationListener />
      <AppLockProvider>
        <RootNavigator />
      </AppLockProvider>
    </NavigationContainer>
  );
};

export default AppNavigator; 